import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportShell, { reportStyle, reportButtonStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { downloadCsv } from '../../../shared/csv';
import { formatCustomerScope, formatProductScope, productScopeTitle, statusLabel } from './PriceTable';
import { formatDate } from '../../../shared/tenantClock';
import { formatMoney as formatMoneyShared, formatNumber, formatPercent } from '../../../shared/numberFormat';
import TaxBasisLine from '../../../shared/TaxBasisLine';

// Forces every header cell to the same 3-line-tall band regardless of its
// own label's actual line count — added once the Base UoM Price columns'
// longer two-line labels made the header row jagged against the still
// one-line ones (SKU, UoM, Effective, Status). bottom-aligned so every
// label's last line still sits flush with the border, matching how a
// naturally 3-line label would read.
const REPORT_HEADER_HEIGHT = { height: 48, lineHeight: '16px', verticalAlign: 'bottom' };

function formatMoney(value) {
  return formatMoneyShared(value);
}

// One row per quantity-break tier (Rule-based or Fixed Override), not one
// row per Price — a collapsed summary (lowest tier + "(+N
// more)") is fine for a quick scan but hides the actual per-quantity
// figures this report exists to show.
//
// A Rule tier is a relative percent/amount-off, not an absolute per-SKU
// price — resolving that against a specific Product's Retail Price
// would need the full pricing engine, out of scope here, so it only ever
// populates the ex-GST column, as a "X% off" / "$X off" label, and leaves
// Ex/Inc GST blank rather than presented as if it were a literal price.
// A Fixed Override break IS a literal absolute (ex-GST stored) price, so
// it gets real Ex GST / Inc GST figures.
function flattenRows(items) {
  const rows = [];
  items.forEach((item) => {
    if (item.rule) {
      const tiers = item.rule.tiers || [];
      if (tiers.length === 0) {
        rows.push({ item, minQuantity: null, ruleLabel: item.rule.name, exPrice: null });
        return;
      }
      tiers.forEach((tier) => {
        const ruleLabel = item.rule.type === 'percent_off'
          ? `${formatPercent(tier.amount)} off`
          : `${formatMoneyShared(tier.amount)} off`;
        rows.push({ item, minQuantity: tier.min_quantity, ruleLabel, exPrice: null });
      });
      return;
    }
    if (item.needs_price) {
      rows.push({ item, minQuantity: null, ruleLabel: 'Needs price', exPrice: null });
      return;
    }
    const breaks = item.breaks || [];
    if (breaks.length === 0) {
      rows.push({ item, minQuantity: null, ruleLabel: '—', exPrice: null });
      return;
    }
    breaks.forEach((brk) => {
      rows.push({ item, minQuantity: brk.min_quantity, ruleLabel: null, exPrice: Number(brk.price) });
    });
  });
  return rows;
}

function formatQty(minQuantity) {
  return formatNumber(minQuantity, { decimals: 0 });
}

// minQuantity is stored in the product's Base UoM (Pieces) — converted
// back to the adjustment's own selling UoM here (rounded up, since a
// partial Pack can't reach a threshold) for the "UoM Qty" column. Only
// resolvable for a specific-Product row with a known conversion rate
// (backend-computed uom_conversion_rate); falls back to the raw Base
// figure for a Product Price Group/All Products row, which has no single
// rate to convert against.
function formatUomQty(minQuantity, item) {
  if (minQuantity === null || minQuantity === undefined) return '';
  if (!item.uom_conversion_rate) return formatQty(minQuantity);
  return formatQty(Math.ceil(Number(minQuantity) / item.uom_conversion_rate));
}

// Break Qty in the Product's Base UoM (e.g. PCS) — already what
// minQuantity is stored as, no conversion needed. Blank for Product Price
// Group/All Products scope, same gating as the Pricing page's own Equiv
// columns (no single conversion rate to confirm the Base UoM name
// against). Named per-row rather than via a shared column header since
// different rows in the same report can have different Base UoMs. Uses
// the UoM's own short `code` (e.g. PCS) rather than its full `name`
// (Pieces) — this column exists to be scanned quickly, not read prose.
function formatBaseQty(minQuantity, item) {
  if (minQuantity === null || minQuantity === undefined || !item.uom_conversion_rate) return '—';
  const baseName = item.product?.base_uom?.code;
  const qty = formatQty(minQuantity);
  return baseName ? `${qty} ${baseName}` : qty;
}

// Break Price converted to a per-Base-UoM-unit price (e.g. $42.50 for a
// Pack of 5 -> $8.50 per PCS) — dividing by conversion_rate rather than
// multiplying, since price scales inversely with how many Base units are
// in the adjustment's own UoM. Same gating/per-row labelling as
// formatBaseQty: blank for a Rule tier (no absolute price to convert) or
// a Product Price Group/All Products row (no single conversion rate).
// incGst additionally requires a default Tax Code to convert against.
function formatBaseUnitPrice(exPrice, item, incGst, defaultTaxRate) {
  if (exPrice === null || !item.uom_conversion_rate) return '—';
  if (incGst && defaultTaxRate === null) return '—';
  const baseName = item.product?.base_uom?.code;
  const basePrice = exPrice / item.uom_conversion_rate;
  const displayPrice = incGst ? basePrice * (1 + defaultTaxRate) : basePrice;
  return baseName ? `${formatMoney(displayPrice)}/${baseName}` : formatMoney(displayPrice);
}

// Full Customer/Group name for this report's section headers — deliberately
// not formatCustomerScope (short_code || name), which the Prices
// list and this report's own CSV still use for a compact column. A section
// header has the room to spell out "Dealer" rather than "DLR".
function formatCustomerScopeFull(item) {
  if (item.customer) return item.customer.name;
  if (item.customer_price_group) return item.customer_price_group.name;
  return 'All Customers';
}

// Splits the flattened per-break rows into one section per Customer, each
// becoming its own headed table with a page break before it in print —
// Customer used to be a repeating column (see git history); a section
// header reads better once there's more than a couple of rows per customer.
// The backend already sorts rows broadest-Customer-first then
// alphabetically, so same-Customer rows are always contiguous — this only
// has to notice where the label changes, not re-sort anything itself.
function groupByCustomer(flatRows) {
  const sections = [];
  flatRows.forEach((row) => {
    const customerLabel = formatCustomerScopeFull(row.item);
    const current = sections[sections.length - 1];
    if (current && current.customerLabel === customerLabel) {
      current.rows.push(row);
    } else {
      sections.push({ customerLabel, rows: [row] });
    }
  });
  return sections;
}

// Product still repeats down consecutive same-Product/different-UoM rows
// within a section — blank it out when identical to the row directly
// above, the same way Customer itself used to repeat down the whole flat
// list. Scoped per-section (not across the whole report) so a new
// Customer's first row always shows its Product, never inherits "blank"
// from the previous section's last row.
function withProductSuppression(rows) {
  let prevProductLabel = null;
  return rows.map((row) => {
    const productLabel = formatProductScope(row.item);
    const showProduct = productLabel !== prevProductLabel;
    prevProductLabel = productLabel;
    return { ...row, productLabel, showProduct };
  });
}

function buildCsvRows(items, defaultTaxRate) {
  return flattenRows(items).map(({ item, minQuantity, ruleLabel, exPrice }) => ({
    Customer: formatCustomerScope(item),
    SKU: formatProductScope(item),
    UoM: item.uom ? item.uom.code : '(All UoMs)',
    'UoM Qty': formatUomQty(minQuantity, item),
    'Base Qty': formatBaseQty(minQuantity, item),
    'Effective From': item.start_date || '',
    'Effective To': item.end_date || '',
    'Price (ex GST)': exPrice !== null ? Number(exPrice).toFixed(2) : ruleLabel,
    'Price (inc GST)': exPrice !== null && defaultTaxRate !== null ? Number(exPrice * (1 + defaultTaxRate)).toFixed(2) : '',
    'Base Price (ex GST)': formatBaseUnitPrice(exPrice, item, false, defaultTaxRate),
    'Base Price (inc GST)': formatBaseUnitPrice(exPrice, item, true, defaultTaxRate),
    Status: statusLabel(item.status),
  }));
}

// Per-quantity-break print/CSV report for the Prices list
// screen (pat-architecture-reports-ui-standard.md). One row per tier/break
// rather than a collapsed summary — a separate Summary variant was
// considered and dropped as not worth maintaining for schedules this short.
function PriceBreakReportView({ items, loading, filterSummary, onClose }) {
  // Same tenant default-rate concept as Retail Prices' / Pricing's own
  // Inc-GST fields — null when no default Tax Code is configured, in which
  // case Inc GST just shows blank rather than guessing a rate.
  const [defaultTaxRate, setDefaultTaxRate] = useState(null);

  useEffect(() => {
    axios.get('/api/tax-codes').then((res) => {
      const defaultCode = (res.data.data || []).find((t) => t.is_default);
      setDefaultTaxRate(defaultCode ? Number(defaultCode.rate) / 100 : null);
    }).catch(() => setDefaultTaxRate(null));
  }, []);

  const sections = loading
    ? []
    : groupByCustomer(flattenRows(items)).map((section) => ({ ...section, rows: withProductSuppression(section.rows) }));
  const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);
  const isEmpty = !loading && totalRows === 0;

  return (
    <ReportShell
      title="Price Break Report"
      subtitle={(
        <>
          <div>{`${formatReportTimestamp()} · ${totalRows} price break${totalRows === 1 ? '' : 's'}`}</div>
          {filterSummary && <div>Filtered by: {filterSummary}</div>}
        </>
      )}
      onClose={onClose}
      printDisabled={loading || isEmpty}
      actions={(
        <button
          type="button"
          onClick={() => downloadCsv('price-break-report.csv', buildCsvRows(items, defaultTaxRate))}
          disabled={loading || isEmpty}
          style={reportButtonStyle(loading || isEmpty)}
        >
          Download CSV
        </button>
      )}
    >
      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</div>
      ) : isEmpty ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>No Prices match the current filters.</div>
      ) : (
        sections.map((section, sIdx) => (
          <div
            key={`${section.customerLabel}-${sIdx}`}
            style={sIdx > 0 ? { breakBefore: 'page', pageBreakBefore: 'always', marginTop: 24 } : undefined}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 15, borderBottom: reportStyle.tableHeaderBorder, paddingBottom: 6 }}>{section.customerLabel}</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize, marginBottom: 20 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>SKU</th>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>UoM</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>UoM<br />Qty</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Base<br />Qty</th>
                  <th style={{ padding: '6px 8px 6px 0', ...REPORT_HEADER_HEIGHT }}>Effective</th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Price<TaxBasisLine>ex GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Price<TaxBasisLine>inc GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Base Price<TaxBasisLine>ex GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 20px 6px 0', textAlign: 'right', ...REPORT_HEADER_HEIGHT }}>Base Price<TaxBasisLine>inc GST</TaxBasisLine></th>
                  <th style={{ padding: '6px 8px 6px 0', textAlign: 'center', ...REPORT_HEADER_HEIGHT }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map(({ item, minQuantity, ruleLabel, exPrice, productLabel, showProduct }, idx) => (
                  <tr key={`${item.id}-${idx}`} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    <td style={{ padding: '6px 8px 6px 0' }} title={productScopeTitle(item)}>{showProduct ? productLabel : ''}</td>
                    <td style={{ padding: '6px 8px 6px 0' }}>{item.uom ? item.uom.code : '(All UoMs)'}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatUomQty(minQuantity, item)}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatBaseQty(minQuantity, item)}</td>
                    <td style={{ padding: '6px 8px 6px 0' }}>{formatDate(item.start_date) || '(any)'} – {formatDate(item.end_date) || '(any)'}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{exPrice !== null ? formatMoney(exPrice) : ruleLabel}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                      {exPrice !== null ? (defaultTaxRate !== null ? formatMoney(exPrice * (1 + defaultTaxRate)) : '—') : ''}
                    </td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'right', color: '#888' }}>{formatBaseUnitPrice(exPrice, item, false, defaultTaxRate)}</td>
                    <td style={{ padding: '6px 20px 6px 0', textAlign: 'right', color: '#888' }}>{formatBaseUnitPrice(exPrice, item, true, defaultTaxRate)}</td>
                    <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>
                      {statusLabel(item.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </ReportShell>
  );
}

export default PriceBreakReportView;
